import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaChevronRight, FaRegTrashAlt } from 'react-icons/fa';
import { InferType, number, object, string } from 'yup';

import {
  AgreementProductReadModel,
  PriceReadModel,
  SetAgreementProductChildPayload,
  useGetProductsByIdChildrenQuery,
} from '@client/api';
import {
  Button,
  InfoTooltip,
  Input,
  isDefinedString,
  TableCell,
  TableRow,
  Text,
  toMoneyStringOrUndefined,
  toPriceString,
  useAreaMetricString,
} from '@client/toolkit';

const schema = object({
  id: string().nullable(),
  priceId: string().required(),
  quantity: number().positive().required(),
  overrideArea: number().nullable(),
  overwrittenUnitAmount: object({
    amount: number().nullable(),
    currency: object({
      code: string().nullable(),
    }).nullable(),
  }).nullable(),
});

type FormValues = InferType<typeof schema>;

export const agreementCommonCostSchema = schema;
export type AgreementCommonCostFormData = FormValues;

export interface ChildOverrideState {
  /** Existing agreement-product child ID (null for new children) */
  agreementChildId?: string | null;
  priceId: string;
  overwrittenUnitAmount?: { amount: number; currency: { code: string } } | null;
}

interface AgreementCommonCostItemFormProps {
  fieldIndex: number;
  productPrice?: PriceReadModel;
  totalArea?: number;
  isFetching?: boolean;
  isInvoiced?: boolean;
  onRemove?: (fieldIndex: number) => void;
  defaultCurrencyCode?: string;
  /** Persisted children from the agreement (for initializing overrides) */
  persistedChildren?: AgreementProductReadModel[];
  /** Callback when children overrides change */
  onChildrenChange?: (fieldIndex: number, children: ChildOverrideState[]) => void;
  /** Callback when user modifies a child override (for dirty tracking) */
  onChildOverrideDirty?: () => void;
  expanded?: boolean;
  onToggleExpanded?: () => void;
  readOnly?: boolean;
}

export function AgreementCommonCostItemForm({
  fieldIndex,
  productPrice: initialPrice,
  totalArea,
  isFetching,
  isInvoiced,
  onRemove,
  defaultCurrencyCode,
  persistedChildren,
  onChildrenChange,
  onChildOverrideDirty,
  expanded = false,
  onToggleExpanded,
  readOnly = false,
}: AgreementCommonCostItemFormProps) {
  const { t: tGlobal } = useTranslation('global');
  const { watch, setValue } = useFormContext();

  const price = initialPrice;
  const productId = price?.product?.id;
  const isFelleskostnader = price?.product?.category === 'Felleskostnader';

  const { data: subProducts } = useGetProductsByIdChildrenQuery(
    { id: productId ?? '' },
    { skip: !productId || !isFelleskostnader },
  );

  const hasSubProducts = (subProducts?.length ?? 0) > 0;

  // Track per-sub-product override amounts (keyed by sub-product catalog ID)
  const [childOverrides, setChildOverrides] = useState<Record<string, string>>(() => {
    // Initialize from persisted children: match by product ID on the price
    const initial: Record<string, string> = {};
    if (persistedChildren) {
      for (const child of persistedChildren) {
        const childProductId = child.price?.product?.id;
        if (childProductId && child.overwrittenUnitAmount?.amount != null) {
          initial[childProductId] = child.overwrittenUnitAmount.amount.toString();
        }
      }
    }
    return initial;
  });

  // Build and notify parent of children state whenever overrides or subProducts change
  useEffect(() => {
    if (!onChildrenChange || !subProducts || subProducts.length === 0) return;
    const children: ChildOverrideState[] = subProducts.map((sub) => {
      // Find persisted child for this sub-product (match by product ID on price)
      const persisted = persistedChildren?.find((c) => c.price?.product?.id === sub.id);
      const overrideStr = childOverrides[sub.id];
      const currencyCode = sub.unitAmount?.currency?.code ?? defaultCurrencyCode ?? 'NOK';
      return {
        agreementChildId: persisted?.id ?? null,
        priceId: sub.priceId ?? '',
        overwrittenUnitAmount:
          overrideStr != null && overrideStr !== ''
            ? { amount: Number(overrideStr), currency: { code: currencyCode } }
            : null,
      };
    });
    onChildrenChange(fieldIndex, children);
  }, [childOverrides, subProducts, persistedChildren]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChildOverrideChange = (subProductId: string, value: string) => {
    setChildOverrides((prev) => ({ ...prev, [subProductId]: value }));
    onChildOverrideDirty?.();
  };
  const overrideArea = watch(`products.${fieldIndex}.overrideArea`);
  const overwrittenAmount = watch(`products.${fieldIndex}.overwrittenUnitAmount.amount`);

  // Effective area: use overrideArea if set, otherwise use totalArea from spaces
  const effectiveArea = overrideArea ?? totalArea ?? 0;

  // Local state for override area input to prevent reset while typing
  const [overrideAreaInput, setOverrideAreaInput] = useState<string>(() => {
    // Initialize: show the override value if set, otherwise empty
    return overrideArea != null ? overrideArea.toString() : '';
  });

  // Sync local state when form value changes externally (e.g., on reset)
  useEffect(() => {
    if (overrideArea == null) {
      setOverrideAreaInput('');
    } else if (overrideAreaInput === '' || Number(overrideAreaInput) !== overrideArea) {
      setOverrideAreaInput(overrideArea.toString());
    }
  }, [overrideArea]); // eslint-disable-line react-hooks/exhaustive-deps

  // Area from Utleieobjekt (passed from parent)
  const areaValue = useMemo(() => {
    if (totalArea == null || totalArea <= 0) return undefined;
    return { metric: totalArea, imperial: 0 };
  }, [totalArea]);
  const areaString = useAreaMetricString(areaValue);

  // Compute parent override from children: sum of (override ?? catalog price) per sub-product
  // Only returns a value when at least one child has an override; otherwise null (use catalog price)
  const computedParentOverride = useMemo(() => {
    if (!hasSubProducts || !subProducts || subProducts.length === 0) return null;
    const hasAnyOverride = subProducts.some((sub) => {
      const v = childOverrides[sub.id];
      return v != null && v !== '';
    });
    if (!hasAnyOverride) return null;
    let sum = 0;
    for (const sub of subProducts) {
      const overrideStr = childOverrides[sub.id];
      const effectiveAmount =
        overrideStr != null && overrideStr !== '' ? Number(overrideStr) : sub.unitAmount?.amount ?? 0;
      sum += effectiveAmount;
    }
    return sum;
  }, [hasSubProducts, subProducts, childOverrides]);

  // Calculate effective price for total
  const effectivePrice = useMemo(() => {
    // When sub-products exist, use the computed sum
    if (hasSubProducts && computedParentOverride != null) {
      return { amount: computedParentOverride, currency: price?.unitAmount?.currency };
    }
    if (overwrittenAmount != null && overwrittenAmount !== '') {
      return { amount: Number(overwrittenAmount), currency: price?.unitAmount?.currency };
    }
    return price?.unitAmount;
  }, [hasSubProducts, computedParentOverride, overwrittenAmount, price?.unitAmount]);

  // Calculate total amount
  const totalAmountString = useMemo(() => {
    if (!effectivePrice?.amount || !effectiveArea) return undefined;
    let total = effectivePrice.amount * effectiveArea;

    // For monthly recurring products, multiply by 12 to get yearly amount
    if (price?.recurring && price?.interval?.type === 'Month') {
      total = total * 12;
    }

    const moneyStr = toMoneyStringOrUndefined({ amount: total, currency: effectivePrice.currency ?? { code: 'NOK' } });
    // Add yearly suffix if recurring, otherwise show as one-time
    const suffix = price?.recurring ? ' / år' : ' Engangs';
    return moneyStr ? `${moneyStr}${suffix}` : undefined;
  }, [effectivePrice, effectiveArea, price?.recurring, price?.interval?.type]);

  const handleOverrideAreaChange = (value: string) => {
    setOverrideAreaInput(value);
    // Set overrideArea to null if empty (use calculated area), otherwise set the override value
    const numValue = value === '' ? null : Number(value);
    setValue(`products.${fieldIndex}.overrideArea`, numValue, { shouldDirty: true });
  };

  const handleOverwrittenPriceChange = (value: string) => {
    if (value === '' || value == null) {
      setValue(`products.${fieldIndex}.overwrittenUnitAmount`, null, { shouldDirty: true });
    } else {
      setValue(
        `products.${fieldIndex}.overwrittenUnitAmount`,
        {
          amount: Number(value),
          currency: { code: price?.unitAmount?.currency?.code ?? defaultCurrencyCode ?? 'NOK' },
        },
        { shouldDirty: true },
      );
    }
  };

  const formatMonthlyPrice = (amount?: number) => {
    if (amount == null) return '-';
    return (
      new Intl.NumberFormat('nb-NO', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount) + ' kr'
    );
  };

  return (
    <>
      <TableRow verticalAlign="middle">
        {/* Produktnavn */}
        <TableCell loading={isFetching}>
          <div className="flex items-center gap-2">
            {hasSubProducts && (
              <button
                type="button"
                className="p-0.5 text-muted hover:text-white transition-colors"
                onClick={onToggleExpanded}
              >
                {expanded ? <FaChevronDown className="w-3 h-3" /> : <FaChevronRight className="w-3 h-3" />}
              </button>
            )}
            <Text as="div" isEmpty={!isDefinedString(price?.product?.name)}>
              <strong>{price?.product?.name}</strong>
            </Text>
          </div>
        </TableCell>

        {/* Areal (from Utleieobjekt - read only) */}
        <TableCell loading={isFetching}>
          <Text as="div" isEmpty={!isDefinedString(areaString)}>
            {areaString}
          </Text>
        </TableCell>

        {/* Overstyrt areal */}
        <TableCell loading={isFetching}>
          <Input
            type="number"
            size="medium"
            placeholder="-"
            value={overrideAreaInput}
            disabled={readOnly}
            onChange={(e) => handleOverrideAreaChange(e.target.value)}
          />
        </TableCell>

        {/* Produktpris (read only) */}
        <TableCell loading={isFetching}>
          <Text as="div" isEmpty={!price?.unitAmount}>
            {toPriceString(price)}
          </Text>
        </TableCell>

        {/* Overstyrt produktpris */}
        <TableCell loading={isFetching}>
          {hasSubProducts ? (
            <Text as="div" className="text-muted text-xs italic">
              {computedParentOverride != null ? formatMonthlyPrice(computedParentOverride) : '-'}
            </Text>
          ) : (
            <Input
              type="number"
              size="medium"
              placeholder="-"
              value={overwrittenAmount ?? ''}
              disabled={readOnly}
              onChange={(e) => handleOverwrittenPriceChange(e.target.value)}
            />
          )}
        </TableCell>

        {/* Fakturert */}
        <TableCell loading={isFetching}>
          {price?.recurring === false && isInvoiced != null ? (
            <Text as="div">{isInvoiced ? tGlobal('yes') : tGlobal('no')}</Text>
          ) : (
            <Text as="div">-</Text>
          )}
        </TableCell>

        {/* Totalt */}
        <TableCell align="right" loading={isFetching}>
          <Text as="div" className="whitespace-nowrap" isEmpty={!isDefinedString(totalAmountString)}>
            <strong>{totalAmountString}</strong>
          </Text>
        </TableCell>

        {/* Delete button */}
        <TableCell loading={isFetching}>
          <InfoTooltip message="Kan ikke slettes pga. aktiv avtale" disabled={typeof onRemove === 'function'}>
            <Button
              size="xs"
              color="primary-invert"
              disabled={typeof onRemove !== 'function'}
              onClick={() => onRemove?.(fieldIndex)}
            >
              <FaRegTrashAlt />
            </Button>
          </InfoTooltip>
        </TableCell>
      </TableRow>

      {/* Sub-product rows */}
      {expanded &&
        hasSubProducts &&
        subProducts?.map((sub, index) => (
          <TableRow key={sub.id} verticalAlign="middle" className="bg-transparent">
            {/* Sub-product name with tree connector */}
            <TableCell>
              <div className="flex items-stretch ml-1.5">
                <div className="flex flex-col items-center w-5 mr-2">
                  <div
                    className={`w-px flex-1 ${index < subProducts.length - 1 ? 'bg-border' : 'bg-transparent'}`}
                    style={{ minHeight: '50%' }}
                  />
                  <div className="flex items-end w-full" style={{ minHeight: '50%' }}>
                    <div className="w-px h-full bg-border" />
                    <div className="h-px flex-1 bg-border" />
                  </div>
                </div>
                <Text as="div" className="text-muted text-xs">
                  {sub.name}
                </Text>
              </div>
            </TableCell>

            {/* Empty area cells */}
            <TableCell />
            <TableCell />

            {/* Sub-product monthly price */}
            <TableCell>
              <Text as="div" className="text-muted text-xs">
                {formatMonthlyPrice(sub.unitAmount?.amount)}
              </Text>
            </TableCell>

            {/* Sub-product override input */}
            <TableCell>
              <Input
                type="number"
                size="medium"
                placeholder="-"
                style={{ fontSize: '12px' }}
                value={childOverrides[sub.id] ?? ''}
                disabled={readOnly}
                onChange={(e) => handleChildOverrideChange(sub.id, e.target.value)}
              />
            </TableCell>

            {/* Empty Fakturert + total + delete cells */}
            <TableCell />
            <TableCell />
            <TableCell />
          </TableRow>
        ))}
    </>
  );
}
